import React from "react";
import Link from "next/link";
import BlogCard from "@/src/features/blog/components/BlogCard";
import type { IBlogListItem } from "@/src/features/blog/types";

interface IAllBlogsProps {
  /** Latest blogs (already limited to 6 by the server page). */
  blogs?: IBlogListItem[];
}

/**
 * Home / campaign "Our blogs" teaser section — max 6 blogs, 3 per row.
 * Renders nothing when there are no blogs so the API never breaks the page.
 */
function AllBlogs({ blogs = [] }: IAllBlogsProps) {
  const items = blogs.slice(0, 6);
  if (items.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 font-sans text-ink-black sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-50 py-1.5 pl-2.5 pr-3.5 text-12 font-semibold uppercase tracking-[0.1em] text-brand-600">
            <span className="h-[5px] w-[5px] rounded-full bg-brand-500" />
            Our blogs
          </div>
          <h2 className="mt-4 text-2xl font-extrabold leading-tight tracking-[-0.035em] md:text-3xl">
            Stories from across Bangladesh
          </h2>
          <p className="mt-4 max-w-[520px] text-16 leading-[1.6] text-ink-secondary [text-wrap:pretty] sm:text-17">
            Practical guides, local insight and honest advice on where to stay —
            written by the Travela community.
          </p>
        </div>

        <Link
          href="/blogs"
          className="inline-flex flex-none items-center gap-2.5 rounded-full border border-[#E6D5DB] px-6 py-3.5 text-14 font-semibold text-ink-black transition-colors duration-300 hover:border-brand-500 hover:text-brand-500"
        >
          View all blogs <span className="text-17 leading-none">→</span>
        </Link>
      </div>

      {/* Grid — 3 per row on desktop */}
      <div className="mt-9 grid grid-cols-1 gap-x-7 gap-y-9 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((blog) => (
          <BlogCard
            key={blog.id}
            slug={blog.slug}
            title={blog.title}
            banner_image={blog.banner_image}
            published_at={blog.published_at}
          />
        ))}
      </div>
    </section>
  );
}

export default AllBlogs;
